import { ResgateSubscriptionManager } from '@/newApi/services/ResgateSubscriptionManager';
import { ResgateManager } from '@/newApi/services/ResgateManager';
import { ResCollection, ResModel, isResError, type ResError } from '@sysarb/resclient';

export class ResErr {
    public rid: string;

    public method: string;

    public params: unknown;

    public message: string;

    public data: { details?: string; reasonId?: string; };

    public constructor(err: ResError);
    public constructor(rid: string, method: string, params: unknown);
    public constructor(arg1: ResError | string, arg2?: string, arg3?: unknown) {
        if (typeof arg1 === 'string') {
            this.rid = arg1;
            this.method = arg2 ?? '';
            this.params = arg3 ?? null;
            this.message = 'Unknown error';
            this.data = { details: '', reasonId: '' };
        } else {
            this.rid = arg1.rid;
            this.method = arg1.method;
            this.params = arg1.params;
            this.message = arg1.message;
            this.data = arg1.data;
        }
    }
}

type ResgateModel = {
    /**
     * Make a 'call' to Resgate.
     *
     * @param rid Resource ID.
     * @param method Method to call.
     * @param data Optional data sent to call.
     * @returns {CallResult<T>}
     */
    call: <T>(rid: string, method: string, data: unknown) => Promise<CallResult<T>>
    /**
     * Get a reactive model from Resgate.
     *
     * @param rid Resource ID to get.
     * @returns {ModelResult<T>}
     */
    model: <T>(rid: string) => Promise<ModelResult<T>>
    /**
     * Get a reactive collection from Resgate.
     *
     * @param rid Resource ID to get.
     * @returns {CollectionResult<T>}
     */
    collection: <T>(rid: string) => Promise<CollectionResult<T>>
    /**
     * Unsubscribe all models and collections used by the current store (if some other store use it, it will not be unsubscribed).
     */
    unsubscribe: () => void;
    unsubscribeSpecific: (rid: string) => void;
};

export type Model<T> = (T & ResModel);

export type Collection<T> = (Iterable<T> & Omit<ResCollection, 'list'> & { list: Model<T>[] });

type ResDataError = {
    success: false;
    error: ResErr;
    data: null;
};

type ModelDataSuccess<T> = {
    success: true;
    error: null;
    data: Model<T> | null;
};

/** Result of a 'model' call. */
export type ModelResult<T> = ModelDataSuccess<T> | ResDataError;

type CollectionDataSuccess<T> = {
    success: true;
    error: null;
    data: Collection<T> | null;
};

/** Result of a 'collection' call. */
export type CollectionResult<T> = CollectionDataSuccess<T> | ResDataError;

type CallDataSuccess<T> = {
    success: true;
    error: null;
    data: T | null;
};

/** Result of a 'call' call. */
export type CallResult<T> = CallDataSuccess<T> | ResDataError;

/** Converts a resgate error to something we can use */
function createError(data: unknown, rid?: string): ResErr {
    if (isResError(data) === true) {
        return new ResErr(data as ResError);
    }

    if (typeof data === 'string') {
        return new ResErr(rid ?? '', '', data);
    }

    return new ResErr(rid ?? '', '', 'Unknown error');
}

/**
 * Handler for integrating with Resgate.
 *
 * @param storeId ID of the store using this.
 * @returns {ResgateModel}
 */
export function useResgate(storeId: string): ResgateModel {
    function subscribe(item: ResModel | ResCollection | ResError): void {
        if (!(item instanceof ResModel) && !(item instanceof ResCollection)) return;

        // model is subscribable, add it to the subscription manager
        ResgateSubscriptionManager.add(storeId, item);
    }

    function unsubscribe(): void {
        // remove all subscriptions for this store
        ResgateSubscriptionManager.remove(storeId);
    }

    function validateSubject(subject?: string | null): boolean {
        if (subject == null) {
            console.error('Subject is required');
            return false;
        }

        if (subject === '') {
            console.error('Subject cannot be empty');
            return false;
        }

        if (subject.startsWith('#')) {
            console.error('Subject cannot start with a hash', subject);
            return false;
        }

        if (subject.includes(' ')) {
            console.error('Subject cannot contain spaces', subject);
            return false;
        }

        if (subject.includes('..')) {
            console.error('Subject cannot contain double dots', subject);
            return false;
        }

        if (subject.includes('.null.')) {
            console.error('Subject cannot contain NULL', subject);
            return false;
        }

        return true;
    }

    function validateConnection(rid: string): ResDataError | null {
        if (validateSubject(rid) === false) {
            return { success: false, data: null, error: createError('Invalid subject', rid) };
        }

        return null;
    }

    async function model<T>(rid: string): Promise<ModelResult<T>> {
        const invalid = validateConnection(rid);

        if (invalid != null) {
            return invalid;
        }

        try {
            const response = await ResgateManager.client.get(rid);

            if (response instanceof ResCollection) {
                throw new Error('Expected model or error but got collection');
            }

            if (response instanceof ResModel) {
                subscribe(response);

                return {
                    success: true,
                    data: response as Model<T>,
                    error: null
                };
            }

            return { success: false, data: null, error: createError(response) };
        } catch (e: unknown) {
            console.error('Error getting model', e);
            return { success: false, data: null, error: createError(e) };
        }
    }

    async function collection<T>(rid: string): Promise<CollectionResult<T>> {
        const invalid = validateConnection(rid);

        if (invalid != null) {
            return invalid;
        }

        try {
            const response = await ResgateManager.client.get(rid);

            if (response instanceof ResModel) {
                throw new Error('Expected collection or error but got model');
            }

            if (response instanceof ResCollection) {
                subscribe(response);

                return {
                    success: true,
                    data: response as Collection<T>,
                    error: null
                };
            }

            return { success: false, data: null, error: createError(response) };
        } catch (e: unknown) {
            return { success: false, data: null, error: createError(e) };
        }
    }

    async function call<T>(rid: string, method: string, data?: unknown): Promise<CallResult<T>> {
        const invalid = validateConnection(rid);

        if (invalid != null) {
            return invalid;
        }

        if (method === '') {
            return { success: false, data: null, error: createError('Method cannot be empty', rid) };
        }

        try {
            const response = await ResgateManager.client.call(rid, method, data).catch((e: unknown) => e) as Promise<T | ResError>;

            if (response instanceof ResModel || response instanceof ResCollection) {
                subscribe(response);
            }

            if (isResError(response) === true) {
                return { success: false, data: null, error: createError(response) };
            }

            return { success: true, data: response as T, error: null };
        } catch (e: unknown) {
            return { success: false, data: null, error: createError(e) };
        }
    }

    function unsubscribeSpecific(rid: string): void {
        ResgateSubscriptionManager.unsubscribeSpecific(storeId, rid);
    }

    return {
        model,
        collection,
        call,
        unsubscribe,
        unsubscribeSpecific
    };
}
